// Ngăn "Đơn nháp" trong màn bán hàng — dùng chung dữ liệu với màn /pos/drafts (M5)
import { useState } from 'react';
import { Modal, message } from 'antd';
import { Link } from 'react-router-dom';
import { Check, Clock, FolderOpen, Lock, PackageOpen, Pen, Trash2, Unlock, X } from 'lucide-react';
import type { Draft } from '../data';
import usePosStore from '../store';
import { draftDaysLeft, draftLockInfo, fmtDateTime, formatDaysLeft, money, usePosAuth } from '../utils';

interface Props {
  open: boolean;
  branchId: string;
  activeDraftId: string | null;
  onClose: () => void;
  onOpenDraft: (d: Draft) => void;
}

export default function DraftsModal({ open, branchId, activeDraftId, onClose, onOpenDraft }: Props) {
  const drafts = usePosStore(s => s.drafts);
  const customers = usePosStore(s => s.customers);
  const ttl = usePosStore(s => s.config.draftTtlDays);
  const renameDraft = usePosStore(s => s.renameDraft);
  const deleteDrafts = usePosStore(s => s.deleteDrafts);
  const unlockDraft = usePosStore(s => s.unlockDraft);
  const { can, userName } = usePosAuth();
  const [editing, setEditing] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [modal, ctx] = Modal.useModal();

  const list = drafts.filter(d => d.branchId === branchId).sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
  const canManage = (d: Draft) => d.createdBy === userName || can('delete');

  const saveName = (d: Draft) => {
    const v = name.trim();
    if (!v) return message.error('Tên không được để trống');
    if (v.length > 60) return message.error('Tên tối đa 60 ký tự');
    renameDraft(d.id, v);
    setEditing(null);
    message.success('Đã đổi tên');
  };

  return (
    <Modal open={open} onCancel={onClose} footer={null} width={760} centered title={`Đơn nháp – ${list.length} đơn`}
      styles={{ body: { maxHeight: '70vh', overflowY: 'auto' } }}>
      {ctx}
      {list.length === 0 ? (
        <div className="flex flex-col items-center py-10 text-slate-500"><PackageOpen className="mb-3 h-12 w-12 text-slate-300" />Không có đơn nháp nào.</div>
      ) : (
        <div className="space-y-3 pt-1">
          {list.map(d => {
            const lock = draftLockInfo(d, userName);
            const days = draftDaysLeft(d.updatedAt, ttl);
            const customer = customers.find(c => c.id === d.customerId);
            return (
              <div key={d.id} className={`rounded-xl border p-4 ${d.id === activeDraftId ? 'border-[#3e91ab] bg-[#ecf6f9]' : 'border-slate-200 bg-white'} ${lock.locked ? 'opacity-80' : ''}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    {editing === d.id ? (
                      <div className="flex items-center gap-1">
                        <input autoFocus value={name} maxLength={60} onChange={e => setName(e.target.value)}
                          onKeyDown={e => { if (e.key === 'Enter') saveName(d); if (e.key === 'Escape') { e.stopPropagation(); setEditing(null); } }}
                          className="h-10 w-full max-w-xs rounded-lg border border-[#3e91ab] px-3 outline-none" />
                        <button onClick={() => saveName(d)} className="flex h-10 w-10 items-center justify-center text-green-600"><Check className="h-5 w-5" /></button>
                        <button onClick={() => setEditing(null)} className="flex h-10 w-10 items-center justify-center text-slate-400"><X className="h-5 w-5" /></button>
                      </div>
                    ) : (
                      <button disabled={!canManage(d)} onClick={() => { setEditing(d.id); setName(d.name); }} className="flex items-center gap-2 text-left text-base font-bold text-slate-800 hover:text-[#2c6b7f] disabled:hover:text-slate-800">
                        {d.name} {canManage(d) && <Pen className="h-3.5 w-3.5 text-slate-400" />}
                      </button>
                    )}
                    <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500">
                      <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{fmtDateTime(d.updatedAt)}</span>
                      <span>{d.lines.length} dòng</span>
                      <span className="font-semibold text-slate-700">{money(d.total)}đ</span>
                      <span>{customer?.name || d.appointment?.customer?.name || 'Khách lẻ'}</span>
                      <span>Tạo bởi {d.createdBy}</span>
                    </div>
                  </div>
                  <div className="flex shrink-0 flex-col items-end gap-1 text-xs">
                    <span className={days < 1 ? 'rounded bg-red-50 px-2 py-0.5 font-bold text-red-600' : 'text-slate-400'}>{formatDaysLeft(days)}</span>
                    {lock.locked && <span className="flex items-center gap-1 rounded border border-amber-200 bg-amber-50 px-2 py-0.5 text-amber-700"><Lock className="h-3 w-3" />{lock.by} đang mở</span>}
                    {d.id === activeDraftId && <span className="font-semibold text-[#2c6b7f]">Đang mở trên máy này</span>}
                  </div>
                </div>
                <div className="mt-3 flex items-center justify-end gap-2 border-t border-slate-100 pt-3">
                  {lock.locked && can('delete') && (
                    <button onClick={() => modal.confirm({
                      title: 'Mở khóa cưỡng bức?', content: `${lock.by} đang mở đơn nháp này. Mở khóa sẽ khiến thay đổi chưa lưu của họ bị mất.`,
                      okText: 'Mở khóa', cancelText: 'Hủy', onOk: () => { unlockDraft(d.id); message.success('Đã mở khóa đơn nháp'); },
                    })} className="flex h-10 items-center gap-1 rounded-lg border border-amber-200 px-3 text-sm font-medium text-amber-700 hover:bg-amber-50">
                      <Unlock className="h-4 w-4" /> Mở khóa
                    </button>
                  )}
                  {canManage(d) && (
                    <button onClick={() => modal.confirm({
                      title: 'Xóa đơn nháp?', content: d.name, okText: 'Xóa', okButtonProps: { danger: true }, cancelText: 'Hủy',
                      onOk: () => { deleteDrafts([d.id]); message.success('Đã xóa đơn nháp'); },
                    })} className="flex h-10 items-center gap-1 rounded-lg px-3 text-sm font-medium text-red-500 hover:bg-red-50">
                      <Trash2 className="h-4 w-4" /> Xóa
                    </button>
                  )}
                  <button disabled={lock.locked || d.id === activeDraftId} onClick={() => onOpenDraft(d)}
                    className="flex h-10 items-center gap-1.5 rounded-lg bg-[#357f96] px-4 text-sm font-semibold text-white hover:bg-[#2c6b7f] disabled:cursor-not-allowed disabled:bg-slate-200 disabled:text-slate-400">
                    <FolderOpen className="h-4 w-4" /> Mở tiếp
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
      <div className="mt-4 text-right text-sm"><Link to="/pos/drafts" className="text-[#2c6b7f] hover:underline">Quản lý tất cả đơn nháp ›</Link></div>
    </Modal>
  );
}
